import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import useFocusRecord from '../utils/useFocusRecord';
import { canEditRecord } from '../utils/roles';
import { hasPermission } from '../utils/permissions';
import { useSortableData } from '../hooks/useSortableData';
import api from '../utils/api';
import { formatDateDMY } from '../utils/dateFormat';
import SearchableSelect from '@/components/ui/SearchableSelect';
import FindDuplicatesButton from '../components/FindDuplicatesButton';

export default function Customers() {
  const { user } = useAuth(); // Use AuthContext instead of localStorage
  const [customers, setCustomers] = useState([]);
  const [divisions, setDivisions] = useState([]);  // NEW: For division dropdown
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showSerialsModal, setShowSerialsModal] = useState(false);
  const [serialsData, setSerialsData] = useState(null);
  const [serialsLoading, setSerialsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Multi-filter state
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterVertical, setFilterVertical] = useState('all');
  const [filterWarranty, setFilterWarranty] = useState('all'); // 'all' | 'has' | 'none' | 'expiring' (within 30d)
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', region: '', business_vertical: '', contact_person: '', 
    vat_reg_no: '', cr_no: '', division: '', credit_period_days: 30  // NEW: Added new fields
  });
  const [editFormData, setEditFormData] = useState({
    name: '', email: '', phone: '', address: '', region: '', business_vertical: '', contact_person: '',
    vat_reg_no: '', cr_no: '', division: '', credit_period_days: 30  // NEW: Added new fields
  });

  const fetchDivisions = async () => {
    try {
      const response = await api.get('/master-data/divisions');
      setDivisions(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchCustomers();
    fetchDivisions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/customers', formData);
      fetchCustomers();
      setShowForm(false);
      setFormData({ name: '', email: '', phone: '', address: '', region: '', business_vertical: '', contact_person: '', vat_reg_no: '', cr_no: '', division: '', credit_period_days: 30 });
      alert('Customer created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving customer');
    }
  };

  const openSerialsModal = async (customer) => {
    setSelectedCustomer(customer);
    setShowSerialsModal(true);
    setSerialsLoading(true);
    setSerialsData(null);
    try {
      const res = await api.get(`/customers/${customer.id}/serials`);
      setSerialsData(res.data);
    } catch (e) {
      setSerialsData({ error: e.response?.data?.detail || 'Failed to load serials' });
    } finally {
      setSerialsLoading(false);
    }
  };

  const handleEditClick = (customer) => {
    setSelectedCustomer(customer);
    setEditFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone || '',
      address: customer.address || '',
      region: customer.region || '',
      business_vertical: customer.business_vertical || '',
      contact_person: customer.contact_person || '',
      vat_reg_no: customer.vat_reg_no || '',
      cr_no: customer.cr_no || '',
      division: customer.division || '',
      credit_period_days: customer.credit_period_days ?? 30,
    });
    setShowEditModal(true);
  };

  // Diagnostics "Open ↗" deep-link: auto-open this customer's edit modal
  // when ?focus=<id> is present.
  useFocusRecord(customers, handleEditClick);

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/customers/${selectedCustomer.id}`, editFormData);
      alert('Customer updated successfully!');
      fetchCustomers();
      setShowEditModal(false);
      setSelectedCustomer(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error updating customer');
    }
  };

  // Admin-only: hard delete a customer
  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`Delete customer "${customer.name}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${customer.id}`);
      alert('Customer deleted successfully!');
      fetchCustomers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting customer');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/customers/template/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to download template');
    }
  };

  const handleImportClick = () => {
    setImportResult(null);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading the same file later
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await api.post('/customers/import/excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
      fetchCustomers();
    } catch (error) {
      setImportResult({
        imported: 0,
        skipped: 0,
        total_rows: 0,
        errors: [error.response?.data?.detail || 'Import failed'],
      });
    } finally {
      setImporting(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    if (filterRegion !== 'all' && (customer.region || '') !== filterRegion) return false;
    if (filterVertical !== 'all' && (customer.business_vertical || '') !== filterVertical) return false;
    if (filterWarranty !== 'all') {
      const sales = Array.isArray(customer.sales) ? customer.sales : [];
      const today = new Date();
      const in30 = new Date(); in30.setDate(today.getDate() + 30);
      const warrantyDates = sales.map(s => s.warranty_expiry).filter(Boolean).map(d => new Date(d));
      const hasWarranty = warrantyDates.length > 0;
      const expiringSoon = warrantyDates.some(d => d >= today && d <= in30);
      if (filterWarranty === 'has' && !hasWarranty) return false;
      if (filterWarranty === 'none' && hasWarranty) return false;
      if (filterWarranty === 'expiring' && !expiringSoon) return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(query) ||
      customer.email?.toLowerCase().includes(query) ||
      customer.phone?.toLowerCase().includes(query) ||
      customer.region?.toLowerCase().includes(query) ||
      customer.business_vertical?.toLowerCase().includes(query) ||
      customer.contact_person?.toLowerCase().includes(query)
    );
  });

  // Auto-curate dropdown options from the existing data
  const regionOptions = Array.from(new Set(customers.map(c => c.region).filter(Boolean))).sort();
  const verticalOptions = Array.from(new Set(customers.map(c => c.business_vertical).filter(Boolean))).sort();

  const {
    sortedItems: sortedCustomers,
    sortKey: custSortKey,
    sortDir: custSortDir,
    requestSort: requestCustSort,
  } = useSortableData(filteredCustomers);
  const custIndicator = (key) => custSortKey !== key ? '↕' : custSortDir === 'asc' ? '▲' : '▼';

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div>Loading...</div></div>;

  return (
    <div className="w-screen max-w-full overflow-x-hidden overflow-y-auto">
      {/* Customers page - No PageHeader on mobile */}
      <div className="w-full px-2 sm:px-4 lg:px-6 py-3 sm:py-6 overflow-x-hidden max-w-full">
        {/* Desktop only header */}
        <div className="hidden lg:block mb-4">
          <PageHeader title="👥 Customer Management">
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => window.location.href = '/dashboard'} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm" data-testid="back-to-dashboard-btn">
                Back to Dashboard
              </button>
              <button onClick={handleDownloadTemplate} className="px-3 py-2 border border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 text-sm" data-testid="download-template-btn">
                📥 Download Template
              </button>
              <button onClick={handleImportClick} disabled={importing} className="px-3 py-2 border border-green-600 text-green-700 rounded-lg hover:bg-green-50 text-sm disabled:opacity-50" data-testid="import-excel-btn">
                {importing ? '⏳ Importing…' : '📤 Import from Excel'}
              </button>
              <FindDuplicatesButton
                module="customers"
                labelFields={['name', 'email', 'phone', 'contact_person', 'country']}
                onAfterDelete={fetchCustomers}
              />
              {hasPermission(user, 'customers', 'create') && (
                <button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-blue-700 to-green-700 text-white px-4 py-2 rounded-lg text-sm" data-testid="add-customer-btn">+ Add Customer</button>
              )}
            </div>
          </PageHeader>
        </div>
        
        {/* Mobile only: Add Customer + Import buttons */}
        <div className="lg:hidden mb-3 flex flex-col gap-2">
          {hasPermission(user, 'customers', 'create') && (
            <button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-blue-700 to-green-700 text-white px-4 py-2 rounded-lg text-sm w-full">+ Add Customer</button>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={handleDownloadTemplate} className="px-3 py-2 border border-blue-600 text-blue-700 rounded-lg text-sm" data-testid="download-template-btn-mobile">
              📥 Template
            </button>
            <button onClick={handleImportClick} disabled={importing} className="px-3 py-2 border border-green-600 text-green-700 rounded-lg text-sm disabled:opacity-50" data-testid="import-excel-btn-mobile">
              {importing ? '⏳…' : '📤 Import Excel'}
            </button>
          </div>
        </div>

        {/* Hidden file input shared by desktop + mobile */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileSelected}
          className="hidden"
          data-testid="import-file-input"
        />

        {/* Import result banner */}
        {importResult && (
          <div className={`mb-3 rounded-lg border p-3 text-sm ${importResult.imported > 0 ? 'bg-green-50 border-green-300 text-green-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`} data-testid="import-result">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold mb-1">
                  {importResult.imported > 0 ? '✅ Import complete' : 'ℹ️ Import finished'}
                </div>
                <div>
                  Imported: <b>{importResult.imported}</b> · Skipped: <b>{importResult.skipped || 0}</b> · Total rows: <b>{importResult.total_rows || 0}</b>
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer underline">View {importResult.errors.length} message(s)</summary>
                    <ul className="list-disc pl-5 mt-1 max-h-40 overflow-y-auto">
                      {importResult.errors.map((err, i) => (<li key={i}>{err}</li>))}
                    </ul>
                  </details>
                )}
              </div>
              <button onClick={() => setImportResult(null)} className="text-gray-500 hover:text-gray-700" data-testid="dismiss-import-result-btn">✕</button>
            </div>
          </div>
        )}

        {/* Search + filter bar */}
        <div className="mobile-section space-y-2" data-testid="customers-filter-bar">
          <input
            type="text"
            placeholder="🔍 Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mobile-search"
            data-testid="customers-search-input"
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <select value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="customers-filter-region">
              <option value="all">All regions</option>
              {regionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={filterVertical} onChange={(e) => setFilterVertical(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="customers-filter-vertical">
              <option value="all">All verticals</option>
              {verticalOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select value={filterWarranty} onChange={(e) => setFilterWarranty(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="customers-filter-warranty">
              <option value="all">All warranties</option>
              <option value="has">Has warranty</option>
              <option value="expiring">Expiring (≤30 days)</option>
              <option value="none">No warranty</option>
            </select>
            {(searchQuery || filterRegion !== 'all' || filterVertical !== 'all' || filterWarranty !== 'all') && (
              <button
                type="button"
                onClick={() => { setSearchQuery(''); setFilterRegion('all'); setFilterVertical('all'); setFilterWarranty('all'); }}
                className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                data-testid="customers-clear-filters-btn"
              >
                Clear filters
              </button>
            )}
          </div>
          <p className="text-xs text-gray-600">
            Showing <b>{filteredCustomers.length}</b> of <b>{customers.length}</b> customers
          </p>
        </div>

        {showForm && (
          <div className="mobile-modal" data-testid="create-customer-modal">
            <div className="mobile-modal-content">
              <div className="mobile-modal-header">
                <h2 className="text-xl sm:text-2xl font-bold">Add New Customer</h2>
              </div>
              <form onSubmit={handleSubmit} className="mobile-modal-body" data-testid="create-customer-form">
                <div className="mobile-form">
                  <div><label className="block text-sm mb-1 font-medium">Name *</label><input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="mobile-input" data-testid="customer-name-input" /></div>
                  <div><label className="block text-sm mb-1 font-medium">Email *</label><input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="mobile-input" data-testid="customer-email-input" /></div>
                  <div><label className="block text-sm mb-1 font-medium">Phone</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="mobile-input" data-testid="customer-phone-input" /></div>
                  <div><label className="block text-sm mb-1 font-medium">Contact Person</label><input type="text" value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} className="mobile-input" data-testid="customer-contact-input" /></div>
                  <div><label className="block text-sm mb-1 font-medium">VAT REG NO.</label><input type="text" value={formData.vat_reg_no} onChange={(e) => setFormData({...formData, vat_reg_no: e.target.value})} className="mobile-input" data-testid="customer-vat-input" placeholder="e.g., BH123456789" /></div>
                  <div><label className="block text-sm mb-1 font-medium">CR NO.</label><input type="text" value={formData.cr_no} onChange={(e) => setFormData({...formData, cr_no: e.target.value})} className="mobile-input" data-testid="customer-cr-input" placeholder="e.g., CR-12345" /></div>
                  <div><label className="block text-sm mb-1 font-medium">Region</label><input type="text" value={formData.region} onChange={(e) => setFormData({...formData, region: e.target.value})} className="mobile-input" data-testid="customer-region-input" /></div>
                  <div><label className="block text-sm mb-1 font-medium">Business Vertical</label><input type="text" value={formData.business_vertical} onChange={(e) => setFormData({...formData, business_vertical: e.target.value})} className="mobile-input" data-testid="customer-business-input" /></div>
                  <div><label className="block text-sm mb-1 font-medium">Customer Division</label><SearchableSelect value={formData.division} onChange={(val) => setFormData({...formData, division: val})} options={divisions.map(div => ({ value: div.name, label: div.name }))} placeholder="Select Division" /></div>
                  <div>
                    <label className="block text-sm mb-1 font-medium">Credit Period (days)</label>
                    <input
                      type="number"
                      min="0"
                      max="365"
                      value={formData.credit_period_days ?? 30}
                      onChange={(e) => setFormData({...formData, credit_period_days: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                      className="mobile-input"
                      data-testid="customer-credit-period-input"
                      placeholder="30"
                    />
                    <p className="text-xs text-gray-500 mt-1">Used to calculate invoice due dates</p>
                  </div>
                </div>
                <div className="mt-4"><label className="block text-sm mb-1 font-medium">Address</label><textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="mobile-input" rows="2" data-testid="customer-address-input" /></div>
              </form>
              <div className="mobile-modal-footer">
                <button type="button" onClick={() => setShowForm(false)} className="mobile-btn border border-gray-300 w-full sm:w-auto" data-testid="cancel-create-btn">Cancel</button>
                <button type="submit" onClick={handleSubmit} className="mobile-btn bg-gradient-to-r from-blue-700 to-green-700 text-white w-full sm:w-auto" data-testid="submit-create-customer-btn">Create Customer</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Customer Modal */}
        {showEditModal && selectedCustomer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="edit-customer-modal">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">Edit Customer Details</h2>
              <form onSubmit={handleUpdateCustomer} className="space-y-4" data-testid="edit-customer-form">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm mb-1">Name *</label><input type="text" required value={editFormData.name} onChange={(e) => setEditFormData({...editFormData, name: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                  <div><label className="block text-sm mb-1">Email *</label><input type="email" required value={editFormData.email} onChange={(e) => setEditFormData({...editFormData, email: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                  <div><label className="block text-sm mb-1">Phone</label><input type="tel" value={editFormData.phone} onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                  <div><label className="block text-sm mb-1">Contact Person</label><input type="text" value={editFormData.contact_person} onChange={(e) => setEditFormData({...editFormData, contact_person: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                  <div><label className="block text-sm mb-1">VAT REG NO.</label><input type="text" value={editFormData.vat_reg_no} onChange={(e) => setEditFormData({...editFormData, vat_reg_no: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., BH123456789" /></div>
                  <div><label className="block text-sm mb-1">CR NO.</label><input type="text" value={editFormData.cr_no} onChange={(e) => setEditFormData({...editFormData, cr_no: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., CR-12345" /></div>
                  <div><label className="block text-sm mb-1">Region</label><input type="text" value={editFormData.region} onChange={(e) => setEditFormData({...editFormData, region: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                  <div><label className="block text-sm mb-1">Business Vertical</label><input type="text" value={editFormData.business_vertical} onChange={(e) => setEditFormData({...editFormData, business_vertical: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                  <div><label className="block text-sm mb-1">Customer Division</label><SearchableSelect value={editFormData.division} onChange={(val) => setEditFormData({...editFormData, division: val})} options={divisions.map(div => ({ value: div.name, label: div.name }))} placeholder="Select Division" /></div>
                  <div>
                    <label className="block text-sm mb-1">Credit Period (days)</label>
                    <input
                      type="number"
                      min="0"
                      max="365"
                      value={editFormData.credit_period_days ?? 30}
                      onChange={(e) => setEditFormData({...editFormData, credit_period_days: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                      className="w-full border rounded px-3 py-2"
                      data-testid="edit-customer-credit-period-input"
                    />
                  </div>
                </div>
                <div><label className="block text-sm mb-1">Address</label><textarea value={editFormData.address} onChange={(e) => setEditFormData({...editFormData, address: e.target.value})} className="w-full border rounded px-3 py-2" rows="2" /></div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">💾 Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Table with scroll indicator */}
        <div className="bg-white rounded-lg shadow">
          {/* Scroll hint */}
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-sm text-blue-700 flex items-center gap-2">
            <span>👉</span>
            <span className="font-medium">Scroll right to see all columns including Actions</span>
          </div>
          
          {filteredCustomers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchQuery ? 'No customers found matching your search' : 'No customers yet'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: '900px' }} data-testid="customers-table">
                <thead className="bg-gradient-to-r from-blue-700 to-green-700 text-white">
                  <tr>
                    <th onClick={() => requestCustSort('name')} className="px-2 py-2 text-left text-sm whitespace-nowrap cursor-pointer select-none hover:bg-black/10" data-testid="sort-customer-name">Name <span className="text-[10px] opacity-80">{custIndicator('name')}</span></th>
                    <th onClick={() => requestCustSort('email')} className="px-2 py-2 text-left text-sm whitespace-nowrap cursor-pointer select-none hover:bg-black/10" data-testid="sort-customer-email">Email <span className="text-[10px] opacity-80">{custIndicator('email')}</span></th>
                    <th onClick={() => requestCustSort('phone')} className="px-2 py-2 text-left text-sm whitespace-nowrap cursor-pointer select-none hover:bg-black/10" data-testid="sort-customer-phone">Phone <span className="text-[10px] opacity-80">{custIndicator('phone')}</span></th>
                    <th onClick={() => requestCustSort('region')} className="px-2 py-2 text-left text-sm whitespace-nowrap cursor-pointer select-none hover:bg-black/10" data-testid="sort-customer-region">Region <span className="text-[10px] opacity-80">{custIndicator('region')}</span></th>
                    <th onClick={() => requestCustSort('business_vertical')} className="px-2 py-2 text-left text-sm whitespace-nowrap cursor-pointer select-none hover:bg-black/10" data-testid="sort-customer-business">Business <span className="text-[10px] opacity-80">{custIndicator('business_vertical')}</span></th>
                    <th className="px-2 py-2 text-center bg-yellow-300 text-black text-sm font-bold whitespace-nowrap sticky right-0">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCustomers.map((c, index) => (
                    <tr key={c.id} className="border-b hover:bg-gray-50" data-testid={`customer-row-${index}`}>
                      <td className="px-2 py-2 font-medium text-sm whitespace-nowrap">{c.name}</td>
                      <td className="px-2 py-2 text-sm truncate" title={c.email} style={{ maxWidth: '200px' }}>{c.email}</td>
                      <td className="px-2 py-2 text-sm whitespace-nowrap">{c.phone || '-'}</td>
                      <td className="px-2 py-2 text-sm whitespace-nowrap">{c.region || '-'}</td>
                      <td className="px-2 py-2 text-sm whitespace-nowrap">{c.business_vertical || '-'}</td>
                      <td className="px-2 py-2 bg-yellow-100 text-center sticky right-0">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openSerialsModal(c)}
                            className="bg-emerald-600 text-white px-2 py-1.5 rounded hover:bg-emerald-700 text-sm whitespace-nowrap font-medium"
                            data-testid={`view-serials-btn-${index}`}
                            title="View assigned serials"
                          >
                            🔢 Serials
                          </button>
                          {hasPermission(user, 'customers', 'edit') ? (
                            <button
                              onClick={() => handleEditClick(c)}
                              className="bg-blue-600 text-white px-2 py-1.5 rounded hover:bg-blue-700 text-sm whitespace-nowrap font-medium"
                              data-testid={`edit-customer-btn-${index}`}
                            >
                              ✏️ Edit
                            </button>
                          ) : (
                            <span className="text-gray-500 text-sm">View Only</span>
                          )}
                          {hasPermission(user, 'customers', 'delete') && (
                            <button
                              onClick={() => handleDeleteCustomer(c)}
                              className="bg-red-600 text-white px-2 py-1.5 rounded hover:bg-red-700 text-sm whitespace-nowrap font-medium"
                              title="Delete this customer"
                              data-testid={`delete-customer-btn-${index}`}
                            >
                              🗑 Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showSerialsModal && selectedCustomer && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            data-testid="view-serials-modal"
          >
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Serials assigned to {selectedCustomer.name}</h2>
                  {serialsData?.total !== undefined && (
                    <p className="text-sm text-gray-600 mt-1">
                      {serialsData.total} serial{serialsData.total === 1 ? '' : 's'} assigned
                    </p>
                  )}
                </div>
                <button
                  onClick={() => { setShowSerialsModal(false); setSerialsData(null); }}
                  className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                  data-testid="close-serials-modal-btn"
                >×</button>
              </div>
              <div className="p-4 overflow-y-auto">
                {serialsLoading && <p className="text-gray-500">Loading…</p>}
                {serialsData?.error && (
                  <p className="text-red-700 text-sm">{serialsData.error}</p>
                )}
                {!serialsLoading && serialsData && !serialsData.error && serialsData.serials?.length === 0 && (
                  <p className="text-gray-500 text-sm">No serials assigned to this customer yet.</p>
                )}
                {!serialsLoading && serialsData?.serials?.length > 0 && (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-2 py-2 text-left">Product</th>
                        <th className="px-2 py-2 text-left">Serial</th>
                        <th className="px-2 py-2 text-left">Sale Date</th>
                        <th className="px-2 py-2 text-left">Warranty End</th>
                        <th className="px-2 py-2 text-left">Next Maintenance</th>
                        <th className="px-2 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serialsData.serials.map((s, i) => {
                        const badgeClass =
                          s.warranty_status === 'expired'
                            ? 'bg-red-100 text-red-700'
                            : s.warranty_status === 'expiring_soon'
                            ? 'bg-amber-100 text-amber-800'
                            : s.warranty_status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600';
                        const fmt = (d) => (d ? formatDateDMY(d) : '-');
                        return (
                          <tr key={`${s.product_id}-${s.serial_number}-${i}`} className="border-b hover:bg-gray-50" data-testid={`customer-serial-row-${i}`}>
                            <td className="px-2 py-2">
                              <div className="font-medium">{s.product_name}</div>
                              {(s.brand || s.model) && (
                                <div className="text-xs text-gray-500">
                                  {[s.brand, s.model].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-2 font-mono">{s.serial_number}</td>
                            <td className="px-2 py-2">{fmt(s.sale_date)}</td>
                            <td className="px-2 py-2">{fmt(s.customer_warranty_end_date)}</td>
                            <td className="px-2 py-2">{fmt(s.next_maintenance_date)}</td>
                            <td className="px-2 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${badgeClass}`}>
                                {s.warranty_status === 'expiring_soon' ? 'Expiring soon' : s.warranty_status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
